import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
// Import the core monaco library to access its types
import * as monaco from 'monaco-editor';
import { Copy, Download, Check } from 'lucide-react';

// Helper function to detect if the content is a pipe-delimited table
const isPipeTable = (content: string): boolean => {
    if (!content) return false;
    const lines = content.trim().split('\n').map((line: string) => line.trim());

    if (lines.length < 2) return false; // Need at least header and data line

    // Check for separator line pattern (pipes, hyphens, optional colons)
    const separatorRegex = /^\|?(:?-+:?\|)+(:?-+:?)?\|?$/;
     const hasSeparator = lines.some((line: string, index: number) => index > 0 && separatorRegex.test(line)); // Check lines after the first


    if (!hasSeparator) return false; // Must have a valid separator line

    // Simple check: do most lines contain pipes?
    let linesWithPipes = 0;
    lines.forEach((line: string) => {
        // Check if the line contains at least one pipe and splits into more than one segment
        if (line.includes('|') && line.split('|').length > 1) {
            linesWithPipes++;
        }
    });

    // Consider it a table if a significant portion of lines have pipes and there's a separator
    // Or if there are at least 2 lines with pipes (handles cases without explicit header)
    return hasSeparator && (linesWithPipes / lines.length > 0.5 || linesWithPipes >= 2);
};


// Helper function to parse pipe-delimited content into a table structure
interface TableData {
    headers: string[];
    rows: string[][];
}

const parsePipeTable = (content: string): TableData => {
    const lines = content.trim().split('\n').map((line: string) => line.trim());
    const tableData: TableData = {
        headers: [],
        rows: []
    };

    let headerRow: string | null = null;
    let separatorRowIndex: number = -1;

    // Find the separator row and the header row preceding it
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const separatorRegex = /^\|?(:?-+:?\|)+(:?-+:?)?\|?$/;
        if (separatorRegex.test(line)) {
            separatorRowIndex = i;
            if (i > 0) {
                headerRow = lines[i - 1];
            }
            // Break after finding the first separator row
            break;
        }
    }

    // If a header row was identified, parse it
    if (headerRow !== null) {
        // Split by pipe, trim whitespace, and filter out empty strings from leading/trailing pipes
        tableData.headers = headerRow
            .split('|')
            .map((cell: string) => cell.trim())
            .filter((cell: string, index: number, arr: string[]) => cell !== '' || (index > 0 && index < arr.length - 1)); // Keep empty cells between pipes
    }

    // Parse data rows after the separator
    if (separatorRowIndex !== -1) {
        for (let i = separatorRowIndex + 1; i < lines.length; i++) {
            const line = lines[i];
            if (!line) continue; // Skip empty lines

             // Split by pipe, trim whitespace, and filter out empty strings from leading/trailing pipes
            const cells = line
                .split('|')
                .map((cell: string) => cell.trim())
                .filter((cell: string, index: number, arr: string[]) => cell !== '' || (index > 0 && index < arr.length - 1)); // Keep empty cells between pipes

            if (cells.length > 0) {
                 // Ensure row has same number of columns as header, padding with empty strings if necessary
                const expectedColumns = tableData.headers.length > 0 ? tableData.headers.length : cells.length;
                while(cells.length < expectedColumns) {
                    cells.push('');
                }
                 // Trim excess cells if a row has more columns than expected
                cells.splice(expectedColumns);

                tableData.rows.push(cells);
            }
        }
    }

    // If no header was found but rows exist and the first row looks like a potential header, use it
    if (tableData.headers.length === 0 && tableData.rows.length > 0) {
         // Check if the first row has a similar number of columns as the separator implied
         // This is a heuristic, might need refinement based on expected data
        const firstRowCells = tableData.rows[0];
        const separatorColumnCount = separatorRowIndex !== -1 ? lines[separatorRowIndex].split('|').filter(s => s.trim() !== '').length : 0;

        if (separatorColumnCount > 0 && Math.abs(firstRowCells.length - separatorColumnCount) <= 1) {
             tableData.headers = tableData.rows.shift() || []; // Use the first row as header and remove it from rows
             // Re-adjust subsequent rows to match new header length if needed
             tableData.rows = tableData.rows.map(row => {
                 while(row.length < tableData.headers.length) {
                     row.push('');
                 }
                 row.splice(tableData.headers.length);
                 return row;
             });
        }
         // If no separator and no clear first-row-as-header heuristic works, headers remain empty.
    }


    return tableData;
};


const MonacoEditorComponent = ({
    editorCode = "",
    language = '',
    maxHeight = '1000px',
    readOnly = false
}) => {
    // Initial state is set to the provided editorCode
    const [code, setCode] = useState<string>(editorCode);

    const [theme, setTheme] = useState<string>('vs-light');
    const [copied, setCopied] = useState<boolean>(false);
    const [downloaded, setDownloaded] = useState<boolean>(false);
    const [detectedLanguage, setDetectedLanguage] = useState<string>(language);
    const [editorHeight, setEditorHeight] = useState<string>('auto');
    const [isTableContent, setIsTableContent] = useState<boolean>(false); // New state for table detection
    const [tableData, setTableData] = useState<TableData>({ headers: [], rows: [] }); // New state for table data

    // Correctly type the editorRef using the type from the monaco library
    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
    // Correctly type the containerRef
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Effect to update code state and re-detect format/language when editorCode prop changes
     useEffect(() => {
         setCode(editorCode);
         const isTable = isPipeTable(editorCode);
         setIsTableContent(isTable);

         if (isTable) {
              setDetectedLanguage('table'); // Indicate it's a table view
              setTableData(parsePipeTable(editorCode));
         } else {
              // Only detect programming language if it's not a table
              const detected = detectLanguage(editorCode);
              setDetectedLanguage(detected);
              setTableData({ headers: [], rows: [] }); // Clear table data if not a table
         }

     }, [editorCode]);


    // Effect for Monaco Editor height
    useEffect(() => {
        // Only update height if editorRef.current exists and it's not table content
        if (editorRef.current && !isTableContent) {
            updateEditorHeight();
             // Dispose previous listener if exists to avoid memory leaks
            const disposable = editorRef.current.onDidContentSizeChange(updateEditorHeight);
            return () => disposable.dispose();
        }
         // If it becomes table content while editorRef.current exists, clean up listener
        if (editorRef.current) {
             // Add a dummy listener to get a disposable object to return for cleanup
             const disposable = editorRef.current.onDidContentSizeChange(() => {});
             // Dispose the dummy listener's disposable when component unmounts or dependencies change
             // This ensures the previous real listener is cleaned up if state changes to table
             return () => disposable.dispose();
        }
         // No cleanup needed if editorRef.current is null
         return undefined;

    }, [editorRef.current, isTableContent, maxHeight]); // Add isTableContent to dependencies


    // Effect for theme based on parent background
    useEffect(() => {
        updateThemeFromBackground();
        const observer = new ResizeObserver(updateThemeFromBackground);
        // Check if containerRef.current is not null before observing
        if (containerRef.current) {
            const parent = containerRef.current.closest('.custom-dashboard-section');
            if (parent) {
                observer.observe(parent);
            }
        }
        return () => observer.disconnect();
    }, []); // Empty dependency array means this runs once on mount

    // Initial check for table content and language on mount (redundant due to editorCode effect, but kept for clarity/initial render state)
     useEffect(() => {
          const isTable = isPipeTable(editorCode);
          setIsTableContent(isTable);

          if (isTable) {
              setDetectedLanguage('table');
              setTableData(parsePipeTable(editorCode));
          } else {
              const detected = detectLanguage(editorCode);
              setDetectedLanguage(detected);
              setTableData({ headers: [], rows: [] });
          }
     }, []); // Empty dependency array means this runs once on mount


    const detectLanguage = (codeContent: string): string => {
        if (!codeContent) return 'text'; // Default to text if no content

        const firstLine = codeContent.split('\n')[0].trim().toLowerCase();
        const knownLanguages: Record<string, string> = { // Explicitly type knownLanguages
            'python': 'python',
            'javascript': 'javascript',
            'typescript': 'typescript',
            'java': 'java',
            'csharp': 'csharp',
            'cpp': 'cpp',
            'go': 'go',
            'rust': 'rust',
            'swift': 'swift',
            'kotlin': 'kotlin',
            'ruby': 'ruby',
            'php': 'php',
            'scala': 'scala',
            'haskell': 'haskell',
            'lua': 'lua',
            'perl': 'perl',
            'r': 'r',
            'dart': 'dart',
            'elixir': 'elixir',
            'sql': 'sql',
            'html': 'html',
            'css': 'css',
            'puml': 'puml',
            'wsd': 'wsd'
        };

        // Check if the first word is a known language identifier (less reliable)
         const firstWordMatch = firstLine.match(/^(\w+)/);
         // Add checks for firstWordMatch and its content before accessing knownLanguages
         if (firstWordMatch && firstWordMatch[1] && knownLanguages[firstWordMatch[1]]) {
              return knownLanguages[firstWordMatch[1]];
         }


        const languagePatterns = [
            { type: 'javascript', keywords: ['function', 'const', 'let', 'var', 'import', 'export', 'class', '=>', 'return'] },
            { type: 'typescript', keywords: ['interface', 'type', 'namespace', 'declare', 'as', 'enum'] },
            { type: 'python', keywords: ['def', 'class', 'import', 'from', 'if', 'elif', 'else', 'for', 'while', 'try', 'except'] },
            { type: 'html', keywords: ['<!DOCTYPE', '<html', '<head', '<body', '<div', '<span', '<p', '<a', '<img', '<script'] },
            { type: 'css', keywords: ['{', '}', ';', 'color:', 'background:', 'margin:', 'padding:', 'font-', '@media'] },
            { type: 'java', keywords: ['public', 'class', 'static', 'void', 'int', 'String', 'import', 'package'] },
            { type: 'csharp', keywords: ['using', 'namespace', 'class', 'public', 'private', 'static', 'void', 'int', 'string'] },
            { type: 'cpp', keywords: ['#include', 'using namespace', 'int main', 'std::', 'cout', 'cin', 'vector', 'class'] },
            { type: 'go', keywords: ['package', 'import', 'func', 'var', 'const', 'type', 'struct', 'interface', 'go', 'chan'] },
            { type: 'rust', keywords: ['fn', 'let', 'mut', 'impl', 'struct', 'enum', 'trait', 'pub', 'use', 'mod'] },
            { type: 'swift', keywords: ['func', 'var', 'let', 'class', 'struct', 'enum', 'protocol', 'extension', 'guard', 'if let'] },
            { type: 'kotlin', keywords: ['fun', 'val', 'var', 'class', 'object', 'interface', 'when', 'is', 'in', 'companion'] },
            { type: 'ruby', keywords: ['def', 'class', 'module', 'attr_', 'require', 'include', 'extend', 'if', 'unless', 'end'] },
            { type: 'php', keywords: ['<?php', 'function', 'class', 'public', 'private', 'protected', 'echo', '$', '->', '=>'] },
            { type: 'scala', keywords: ['def', 'val', 'var', 'class', 'object', 'trait', 'extends', 'with', 'case', 'match'] },
            { type: 'haskell', keywords: ['module', 'import', 'data', 'type', 'class', 'instance', 'where', 'let', 'in', 'do'] },
            { type: 'lua', keywords: ['function', 'local', 'if', 'then', 'else', 'elseif', 'for', 'while', 'repeat', 'until'] },
            { type: 'perl', keywords: ['sub', 'my', 'use', 'package', 'if', 'elsif', 'unless', 'while', 'foreach', 'print'] },
            { type: 'r', keywords: ['function', '<-', 'if', 'else', 'for', 'while', 'repeat', 'in', 'library', 'data.frame'] },
            { type: 'dart', keywords: ['void', 'var', 'final', 'const', 'class', 'extends', 'implements', 'async', 'await', 'Future'] },
            { type: 'elixir', keywords: ['def', 'defmodule', 'do', 'end', 'if', 'case', 'when', 'fn', '->', 'pipe'] },
            { type: 'sql', keywords: ['SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'TABLE', 'ALTER', 'JOIN'] },
            { type: 'puml', keywords: ['@startuml', '@enduml', 'participant', 'as', 'if', 'else', 'while', 'for', 'repeat', 'alt', 'group'] },
            { type: 'wsd', keywords: ['@startuml', '@enduml', 'participant', 'as', 'if', 'else', 'while', 'for', 'repeat', 'alt', 'group'] },
             // Add markdown keywords to detect it, as tables are common in markdown
            { type: 'markdown', keywords: ['#', '##', '###', '####', '#####', '######', '*', '_', '1.', '-', '`', '[', ']', '(', ')', '![', '>', '```'] }
        ];

        function escapeRegExp(string: string): string { // Added type for string parameter and return
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }

        const scores = languagePatterns.map(pattern => {
            let score = 0;
            pattern.keywords.forEach(keyword => {
                const escapedKeyword = escapeRegExp(keyword);
                 // Use word boundaries \b to match whole keywords
                const regex = new RegExp(`\\b${escapedKeyword}\\b`, 'gi');
                const matches = (codeContent.match(regex) || []).length;
                score += matches;
            });
            return { type: pattern.type, score };
        });

        const bestMatch = scores.reduce((max, current) =>
            current.score > max.score ? current : max
            , { type: 'text', score: 0 }); // Default to 'text' if no strong match

        return bestMatch.type;
    };


    const updateThemeFromBackground = () => {
        // Check if containerRef.current is not null before accessing properties
        if (containerRef.current) {
            const parent = containerRef.current.closest('.custom-dashboard-section');
            if (parent) {
                const bgColor = window.getComputedStyle(parent as Element).backgroundColor; // Cast to Element
                const rgb = bgColor.match(/\d+/g);
                if (rgb && rgb.length === 3) { // Ensure we have R, G, B values
                    const brightness = (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) / 1000;
                    setTheme(brightness > 128 ? 'vs-light' : 'vs-dark');
                }
            }
        }
    };


    const generateHtmlTable = (data: TableData): string => {
        // Use inline styles for borders and colors based on the current theme
        // These should match the CSS defined below
        const borderColor = theme === 'vs-light' ? '#333333' : '#cccccc'; // Dark border for light, light border for dark
        const headerBgColor = theme === 'vs-light' ? 'rgba(240, 240, 240, 0.5)' : 'rgba(30, 30, 30, 0.5)'; // Match CSS
        const headerTextColor = theme === 'vs-light' ? '#333333' : '#cccccc'; // Match CSS
        const evenRowBgColor = theme === 'vs-light' ? 'rgba(240, 240, 240, 0.2)' : 'rgba(30, 30, 30, 0.2)'; // Match CSS
        const defaultTextColor = theme === 'vs-light' ? '#333333' : '#cccccc'; // Default text color for cells

        let html = `<table style="border-collapse: collapse; width: 100%; color: ${defaultTextColor};">`; // Set default text color on table

        // Add headers if they exist
        if (data.headers.length > 0) {
            html += '<thead><tr>';
            data.headers.forEach(header => {
                // Use dynamic border and background/text color for headers
                html += `<th style="border: 1px solid ${borderColor}; padding: 8px; text-align: left; vertical-align: top; background-color: ${headerBgColor}; color: ${headerTextColor}; font-weight: bold;">${escapeHtml(header)}</th>`;
            });
            html += '</tr></thead>';
        }

        // Add body rows
        if (data.rows.length > 0) {
            html += '<tbody>';
            data.rows.forEach((row, rowIndex) => {
                // Apply background color for even rows
                const rowBgColor = rowIndex % 2 === 1 ? evenRowBgColor : 'transparent'; // No specific background for odd rows by default
                html += `<tr style="background-color: ${rowBgColor};">`;
                row.forEach(cell => {
                    // Use dynamic border for data cells
                    html += `<td style="border: 1px solid ${borderColor}; padding: 8px; text-align: left; vertical-align: top;">${escapeHtml(cell)}</td>`;
                });
                html += '</tr>';
            });
            html += '</tbody>';
        }

        html += '</table>';
        return html;
    };

    // (Keep the existing escapeHtml function)
    const escapeHtml = (text: string): string => {
        const map: { [key: string]: string } = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;'
        };
        return text.replace(/[&<>"']/g, (m) => map[m]);
    };

    const copyToClipboard = () => {
        let contentToCopy = '';
        // Copying HTML requires 'text/html' MIME type
        let mimeType = 'text/plain'; // Default MIME type
        let contentAsPlainText = code; // Fallback plain text content

        if (isTableContent && (tableData.headers.length > 0 || tableData.rows.length > 0)) {
            // If it's table content, generate HTML and set MIME type to HTML
            contentToCopy = generateHtmlTable(tableData);
            mimeType = 'text/html';
            // For a table, the plain text fallback could be the original pipe table markdown
             contentAsPlainText = code; // Use original code as plain text fallback
        } else {
            // For non-table content, copy the raw code as plain text
            contentToCopy = code; // Same as plain text in this case
            mimeType = 'text/plain';
            contentAsPlainText = code;
        }


        if (navigator.clipboard.write) {
             // Use ClipboardItem to offer both HTML and plain text formats
             const htmlBlob = new Blob([contentToCopy], { type: 'text/html' });
             const plainTextBlob = new Blob([contentAsPlainText], { type: 'text/plain' });

             const clipboardItem = new ClipboardItem({
                 'text/html': htmlBlob,
                 'text/plain': plainTextBlob
             });

             navigator.clipboard.write([clipboardItem])
                 .then(() => {
                     setCopied(true);
                     setTimeout(() => setCopied(false), 2000);
                 })
                 .catch(err => {
                     console.error('Failed to copy content using ClipboardItem: ', err);
                     // Fallback to writeText if write fails
                     navigator.clipboard.writeText(contentToCopy) // This might paste HTML string or just text depending on app
                        .then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                        })
                        .catch(errText => {
                            console.error('Failed to copy content with writeText fallback: ', errText);
                        });
                 });
         } else {
            // Fallback for browsers that don't support navigator.clipboard.write (older browsers)
            // This will likely paste as plain text or raw HTML string in many applications
            navigator.clipboard.writeText(contentToCopy)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(err => {
                    console.error('Failed to copy content with writeText fallback: ', err);
                });
        }
    };

    const downloadCode = () => {
        // Use the current code state, which reflects the editorCode prop
        const contentToDownload = code;
        // Download table as txt or original lang. Consider downloading HTML for tables?
        // Sticking to original text content for download for consistency with editorCode prop.
        const fileExtension = detectedLanguage === 'table' ? 'txt' : (detectedLanguage || 'txt'); // Download table as txt or original lang

        const element = document.createElement('a');
        const file = new Blob([contentToDownload], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `content.${fileExtension}`; // Adjusted filename
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        setDownloaded(true);
        setTimeout(() => setDownloaded(false), 2000);
    };

    const updateEditorHeight = () => {
        // Check if editorRef.current exists and it's not table content
        if (editorRef.current && !isTableContent) {
            const contentHeight = editorRef.current.getContentHeight();
            const newHeight = Math.max(Math.min(contentHeight, parseInt(maxHeight, 10)), 5); // Ensure minimum height, use radix 10
            setEditorHeight(`${newHeight}px`);
        }
    };


    // Type the editor parameter correctly using the type from the monaco library
    const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
        editorRef.current = editor;
        updateEditorHeight();
    };

     // Render the table view
    const renderTableView = () => (
        <div className="table-view-container" style={{ maxHeight: maxHeight, overflowY: 'auto' }}>
            {tableData.headers.length > 0 || tableData.rows.length > 0 ? (
                 <table className={`pipe-table ${theme === 'vs-dark' ? 'dark-table' : 'light-table'}`}>
                     {tableData.headers.length > 0 && (
                         <thead>
                             <tr>
                                 {tableData.headers.map((header: string, index: number) => (
                                     <th key={index}>{header}</th>
                                 ))}
                             </tr>
                         </thead>
                     )}
                     <tbody>
                         {tableData.rows.map((row: string[], rowIndex: number) => ( // Typed row and rowIndex
                             <tr key={rowIndex}>
                                 {row.map((cell: string, cellIndex: number) => ( // Typed cell and cellIndex
                                     <td key={cellIndex}>{cell}</td>
                                 ))}
                             </tr>
                         ))}
                     </tbody>
                 </table>
            ) : (
                <div className="no-table-data">No table data detected.</div>
            )}
        </div>
    );


    return (
        <div
            className={`custom-dashboard-section monaco-editor-container ${theme}`}
            ref={containerRef}
             style={{
                 // Dynamically set CSS variables for editor/table content based on the theme
                 '--vscode-editor-background': theme === 'vs-light' ? '#ffffff' : '#1e1e1e',
                 '--vscode-editor-foreground': theme === 'vs-light' ? '#333333' : '#cccccc',
                 // Define other variables if needed, e.g., for widget colors
                 // '--vscode-editorWidget-background': theme === 'vs-light' ? '#f3f3f3' : '#252526',
                 // '--vscode-editorWidget-foreground': theme === 'vs-light' ? '#333333' : '#cccccc',
             } as React.CSSProperties}
        >
            <div className="toolbar">
                <div className="language">{isTableContent ? 'Table' : detectedLanguage}</div> {/* Display 'Table' when detected */}
                <div className="buttons">
                     <button onClick={copyToClipboard} title={`Copy ${isTableContent ? 'Table' : 'Code'}`}> {/* Updated title */}
                         {copied ? <Check size={18} /> : <Copy size={18} />}
                     </button>
                    <button onClick={downloadCode} title={`Download ${isTableContent ? 'Table' : 'Code'}`}> {/* Updated title */}
                        <Download size={18} />
                    </button>
                </div>
            </div>
             {isTableContent ? (
                 renderTableView() // Render table if content is detected as a table
             ) : (
                 <Editor
                     height={editorHeight}
                     defaultLanguage={detectedLanguage}
                     value={code}
                     theme={theme}
                     onChange={(value) => setCode(value || '')}
                     onMount={handleEditorDidMount}
                     options={{
                         readOnly: readOnly,
                         minimap: { enabled: true },
                         scrollBeyondLastLine: false,
                         fontSize: 16,
                         tabSize: 2,
                         automaticLayout: true,
                         wordWrap: 'on',
                         lineNumbers: 'on', // Enable line numbers
                         folding: false,
                         padding: { top: 8, bottom: 8 },
                     }}
                 />
             )}
             {/* Optional: Add basic styling for the table view */}
             <style>{`
                 .table-view-container {
                     width: 100%;
                     border: 1px solid var(--vscode-editor-foreground, #cccccc); /* Use editor foreground color for border, with fallback */
                     box-sizing: border-box;
                      padding: 8px; /* Match editor padding */
                     color: var(--vscode-editor-foreground, #cccccc); /* Inherit editor foreground color */
                      background-color: var(--vscode-editor-background, #1e1e1e); /* Inherit editor background color */
                      overflow: auto; /* Ensure table scrolls if needed */
                      resize: vertical; /* Allow vertical resizing */
                      min-height: 50px; /* Minimum height */
                 }
                 .pipe-table {
                     width: 100%;
                     border-collapse: collapse;
                     font-family: var(--vscode-editor-font-family, monospace); /* Use editor font */
                      font-size: var(--vscode-editor-font-size, 16px); /* Use editor font size */
                      color: inherit; /* Inherit text color from container */
                 }
                  .pipe-table th, .pipe-table td {
                      border: 1px solid var(--vscode-editor-foreground, #cccccc); /* Use editor foreground color for border */
                      padding: 8px;
                      text-align: left;
                      vertical-align: top; /* Align cell content to top */
                   }
                   /* Remove generic .pipe-table th background/color to rely purely on theme classes */
                  .pipe-table th {
                      font-weight: bold;
                   }

                   /* Theme-specific header styles */
                   .pipe-table.dark-table th {
                       background-color: rgba(30, 30, 30, 0.5); /* Dark background */
                       color: var(--vscode-editor-foreground, #cccccc); /* Light text */
                   }
                    .pipe-table.light-table th {
                       background-color: rgba(240, 240, 240, 0.5); /* Light background */
                       color: var(--vscode-editor-foreground, #333333); /* Dark text */
                    }

                  .pipe-table tr:nth-child(even) {
                       /* Default even row background - will use --vscode-editor-background from container */
                       background-color: var(--vscode-editor-background, #1e1e1e);
                  }
                   /* More specific even row backgrounds for slight variation */
                   .pipe-table.dark-table tr:nth-child(even) {
                       background-color: rgba(30, 30, 30, 0.2); /* Slightly different dark */
                   }
                    .pipe-table.light-table tr:nth-child(even) {
                       background-color: rgba(240, 240, 240, 0.2); /* Slightly different light */
                    }


                 .no-table-data {
                     text-align: center;
                     padding: 20px;
                     color: var(--vscode-editor-foreground, #cccccc); /* Use editor foreground color */
                 }

                  /* Theme-specific Toolbar styles */
                   .toolbar {
                       display: flex;
                       justify-content: space-between;
                       align-items: center;
                       padding: 4px 8px;
                       font-family: var(--vscode-editor-font-family, monospace);
                       font-size: 14px;
                       /* Remove background/color here, define below by theme */
                        border-bottom: 1px solid; /* Define color by theme below */
                   }

                   .monaco-editor-container.vs-light .toolbar {
                        background-color: #f3f3f3; /* Light background */
                        color: #333333; /* Dark text */
                        border-bottom-color: #dddddd; /* Lighter border */
                   }

                    .monaco-editor-container.vs-dark .toolbar {
                        background-color: #252526; /* Dark background (VS Code widget default) */
                        color: #cccccc; /* Light text (VS Code widget default) */
                        border-bottom-color: #454545; /* Darker border */
                   }


                  .toolbar .language {
                      font-weight: bold;
                  }
                  .toolbar .buttons button {
                      background: none;
                      border: none;
                      cursor: pointer;
                      /* Color inherited from parent toolbar now */
                      color: inherit; /* Important: Inherit color from toolbar */
                      margin-left: 8px;
                      padding: 4px;
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                  }
                   .toolbar .buttons button:hover {
                       /* Use a color that works for both themes, or define theme-specific hover */
                       /* VS Code's link color is often a good hover for text */
                       color: var(--vscode-textLink-foreground, #007acc);
                   }


             `}</style>
        </div>
    );
};

export default MonacoEditorComponent;